/**
 * EventLanding - Public event registration page with premium landing design
 * Mobile-optimized with PWA support
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/store';
import { eventAPI, registrationAPI, paymentAPI, customFieldsAPI, getErrorMessage } from '@/lib/api';
import { externalFormAPI } from '@/lib/api';
import { PublicLayout } from '@/components/layouts';
import { cn } from '@/lib/utils';
import PaymentGatewaySelector from '@/components/PaymentGatewaySelector';
import AdminLoginButton from '@/components/AdminLoginButton';
import AddToCalendar from '@/components/AddToCalendar';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { InstallAppPrompt } from '@/components/ui/install-app-prompt';
import { DigitalTicket } from '@/components/ui/digital-ticket';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { EventLiveSession } from '@/components/live/EventLiveSession';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Icons
import {
  Clock, MapPin, ArrowRight, ChevronLeft, ChevronRight, Check,
  Star, CheckCircle2, Utensils, AlertCircle, Loader2, X, Users, Plus, Trash2,
  UserPlus, Download, Share2, CalendarDays
} from 'lucide-react';

// ============== Meal Selection Form ==============
const MealSelectionForm = ({ event, selections, onChange }) => {
  const startDate = new Date(event.start_date);
  const endDate = new Date(event.end_date);
  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

  const toggleMeal = (day, category) => {
    const existing = selections.find((s) => s.day === day && s.category === category);
    if (existing) {
      onChange(selections.filter((s) => !(s.day === day && s.category === category)));
    } else {
      onChange([...selections, { day, category, dietary: "none", redeemed: false }]);
    }
  };

  const isSelected = (day, category) => {
    return selections.some((s) => s.day === day && s.category === category);
  };

  return (
    <div className="space-y-4">
      {Array.from({ length: Math.min(days, 5) }, (_, i) => i + 1).map((day) => (
        <div key={day} className="p-4 border rounded-lg">
          <h4 className="font-medium mb-3">Day {day}</h4>
          <div className="flex flex-wrap gap-2">
            {["breakfast", "lunch", "dinner"].map((meal) => (
              <Button
                key={meal}
                variant={isSelected(day, meal) ? "default" : "outline"}
                size="sm"
                onClick={() => toggleMeal(day, meal)}
              >
                {isSelected(day, meal) && <Check className="mr-1 h-3 w-3" />}
                {meal.charAt(0).toUpperCase() + meal.slice(1)}
              </Button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============== Event Landing & Registration ==============
// Mirrors BUILT_IN_REGISTRATION_FIELDS in backend/core/registration_fields.py.
// Used only when the event payload predates the resolved config.
const FIELD_FALLBACK_LABELS = {
  phone: 'Phone Number',
  company: 'Company/Organization',
  job_title: 'Job Title',
  country: 'Country',
  city: 'City',
  dietary_requirements: 'Dietary Requirements',
  special_needs: 'Accessibility/Special Needs',
};
const OPTIONAL_FIELD_IDS = Object.keys(FIELD_FALLBACK_LABELS);

// iter 635 — dates, read as local days. `new Date('2026-10-08')` is parsed as
// UTC by the spec and renders as 7 October west of Greenwich.
const publicDay = (value) => {
  const [y, m, d] = String(value || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-US',
    { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

/** "Thu, Oct 8 – Sun, Oct 11, 2026" for a conference, one day for the rest. */
const publicRange = (from, to) => {
  const first = publicDay(from);
  const last = publicDay(to);
  if (!first) return 'Dates to be announced';
  return !last || last === first ? first : `${first} — ${last}`;
};

/** `venue` is a string on newer events and an object on older ones. */
const venueName = (venue) =>
  (venue && typeof venue === 'object' ? (venue.name || venue.address || '') : (venue || ''));


const EventLanding = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0: landing, 1: identity, 2: provisioning, 3: review, 4: payment
  const [registrationType, setRegistrationType] = useState('individual'); // individual or family
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    gender: "",
    phone: "",
    province: "",
    district: "",
    date_of_birth: "",
    age_group: "",
    tier_id: "",
    company: "",
    title: "",
    country: "",
    city: "",
    dietary_requirements: "",
    special_needs: "",
    bio: "",
    interests: [],
    meal_selections: [],
    custom_responses: {}, // For custom form fields
    // Vehicle registration
    addVehicle: false,
    vehicle: null,
  });
  const [familyMembers, setFamilyMembers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: false });
  // Some legacy events point at a poster whose file is gone — the uploads
  // route answers with a 1×1 placeholder, so we check the decoded size.
  const [heroUsable, setHeroUsable] = useState(true);
  const [pendingPayment, setPendingPayment] = useState(null); // Store delegate data for payment

  const { data: event, isLoading } = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => eventAPI.get(eventId).then((r) => r.data),
  });

  const { data: tiers } = useQuery({
    queryKey: ["tiers", eventId],
    queryFn: () => eventAPI.getTiers(eventId).then((r) => r.data),
    enabled: !!event,
  });

  // iter 634 — the registration form builder's toggles, honoured. The public
  // event payload carries `registration_form_config` already resolved by the
  // backend (same table both sides), so a field the organiser switched off is
  // not rendered and one they marked required is starred, gated and enforced.
  const fieldCfg = (id) => {
    const cfg = (event?.registration_form_config || {})[id] || {};
    const enabled = cfg.enabled !== false;
    return {
      enabled,
      required: enabled && cfg.required === true,
      label: cfg.label || FIELD_FALLBACK_LABELS[id] || id,
    };
  };
  const star = (id) => (fieldCfg(id).required ? ' *' : '');
  // Which of the organiser's required fields are still blank. Drives the
  // Continue button, because `required` on an input outside a <form> does
  // nothing on its own.
  const requiredGaps = OPTIONAL_FIELD_IDS.filter((id) => {
    const cfg = fieldCfg(id);
    const value = formData[id === 'job_title' ? 'title' : id];
    return cfg.required && !String(value || '').trim();
  });

  // Fetch custom registration form fields
  const { data: customFields = [] } = useQuery({
    queryKey: ["custom-fields", eventId],
    queryFn: () => customFieldsAPI.list(eventId).then((r) => r.data),
    enabled: !!event,
  });

  // Fetch optional Google Forms / external-form config for this event
  const { data: externalForm } = useQuery({
    queryKey: ["event-external-form-public", eventId],
    queryFn: () => externalFormAPI.publicGet(eventId).then((r) => r.data),
    enabled: !!event,
  });

  // Countdown timer effect
  useEffect(() => {
    if (!event?.start_date) return;
    
    const updateCountdown = () => {
      const now = new Date().getTime();
      const target = new Date(event.start_date).getTime();
      const diff = target - now;
      
      if (diff > 0) {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
          expired: false,
        });
      } else {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [event?.start_date]);

  const registerMutation = useMutation({
    mutationFn: (data) => registrationAPI.register(event.id, data),
    onSuccess: async (response) => {
      const data = response.data;
      if (data.requires_payment) {
        // Get the selected tier price
        const selectedTier = tiers?.find(t => t.id === formData.tier_id);
        const amount = selectedTier?.price || 0;
        
        // Store delegate data and show payment gateway selector
        setPendingPayment({
          delegate_id: data.id,
          event_id: event.id,
          access_token: data.access_token,
          tier_id: formData.tier_id,
          amount: amount,
          currency: event.currency || 'USD',
        });
        
        // Move to payment step
        setStep(4);
        setIsSubmitting(false);
        toast.info("Please complete your payment to confirm registration");
      } else {
        toast.success("Registration complete!");
        useAuthStore.getState().setAuth(
          data.access_token,
          data,
          "delegate",
          event.id
        );
        navigate("/delegate");
      }
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Registration failed"));
      setIsSubmitting(false);
    },
  });

  // Family registration mutation
  const familyRegisterMutation = useMutation({
    mutationFn: (data) => registrationAPI.registerFamily(event.id, data),
    onSuccess: async (response) => {
      const data = response.data;
      if (data.requires_payment) {
        setPendingPayment({
          delegate_id: data.primary_delegate?.id,
          family_group_id: data.family_group_id,
          event_id: event.id,
          access_token: data.access_token,
          amount: data.total_amount,
          currency: event.currency || 'USD',
        });
        setStep(4);
        setIsSubmitting(false);
        toast.info(`Total: ${event.currency || '$'}${data.total_amount.toFixed(2)}. Please complete payment.`);
      } else {
        toast.success(`Family of ${data.family_members_count + 1} registered!`);
        useAuthStore.getState().setAuth(
          data.access_token,
          data.primary_delegate,
          "delegate",
          event.id
        );
        navigate("/delegate");
      }
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Family registration failed"));
      setIsSubmitting(false);
    },
  });

  // Add family member
  const addFamilyMember = () => {
    if (familyMembers.length >= 9) {
      toast.error("Maximum 9 additional family members allowed");
      return;
    }
    setFamilyMembers([...familyMembers, {
      name: "",
      email: "",
      phone: "",
      tier_id: formData.tier_id || "free",
      relationship: "family",
      age_group: "adult"
    }]);
  };

  // Update family member
  const updateFamilyMember = (index, field, value) => {
    const updated = [...familyMembers];
    updated[index] = { ...updated[index], [field]: value };
    setFamilyMembers(updated);
  };

  // Remove family member
  const removeFamilyMember = (index) => {
    setFamilyMembers(familyMembers.filter((_, i) => i !== index));
  };

  // Handle payment completion
  const handlePaymentInitialized = (paymentData) => {
    // Store payment info for tracking
    sessionStorage.setItem('pending_payment', JSON.stringify({
      ...pendingPayment,
      payment_id: paymentData.payment_id,
      transaction_id: paymentData.transaction_id,
    }));
  };

  const handlePaymentComplete = (paymentData) => {
    // For manual payments, show confirmation message
    toast.success("Thank you! Your payment will be verified by the organizer.");
    
    // Log in the user with pending status
    if (pendingPayment?.access_token) {
      localStorage.setItem('eventflow_token', pendingPayment.access_token);
      navigate("/delegate");
    } else {
      navigate(`/events/${eventId}`);
    }
  };

  const handlePaymentCancel = () => {
    // Go back to review step
    setStep(3);
    setPendingPayment(null);
  };

  // Handle cancel registration - reset to landing page
  const handleCancelRegistration = () => {
    setStep(0);
    // Reset family registration state
    setRegistrationType('individual');
    setFamilyMembers([]);
    setPendingPayment(null);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    const idempotencyKey = crypto.randomUUID();
    
    // Combine first and last name for backend
    const fullName = `${formData.first_name} ${formData.last_name}`.trim();
    
    if (registrationType === 'family' && familyMembers.length > 0) {
      // Family registration
      familyRegisterMutation.mutate({
        primary_email: formData.email,
        primary_name: fullName,
        primary_phone: formData.phone,
        primary_gender: formData.gender,
        primary_tier_id: formData.tier_id === 'free' ? 'free' : formData.tier_id,
        password: formData.password,
        company: formData.company,
        family_members: familyMembers.map(m => ({
          name: m.name,
          email: m.email || null,
          phone: m.phone || null,
          gender: m.gender || null,
          tier_id: m.tier_id === 'free' ? 'free' : m.tier_id,
          relationship: m.relationship,
          age_group: m.age_group
        })),
      });
    } else {
      // Individual registration
      registerMutation.mutate({
        event_id: event.id,
        email: formData.email,
        password: formData.password,
        name: fullName,
        gender: formData.gender,
        phone: formData.phone,
        province: formData.province,
        district: formData.district,
        date_of_birth: formData.date_of_birth,
        age_group: formData.age_group,
        tier_id: formData.tier_id === 'free' ? null : formData.tier_id,
        company: formData.company,
        title: formData.title,
        country: formData.country,
        city: formData.city,
        dietary_requirements: formData.dietary_requirements,
        special_needs: formData.special_needs,
        custom_responses: formData.custom_responses,
        idempotency_key: idempotencyKey,
      });
    }
  };

  // Calculate total for family registration
  const calculateFamilyTotal = () => {
    let total = 0;
    // Primary registrant
    if (formData.tier_id && formData.tier_id !== 'free') {
      const primaryTier = tiers?.find(t => t.id === formData.tier_id);
      total += primaryTier?.price || 0;
    }
    // Family members
    familyMembers.forEach(member => {
      if (member.tier_id && member.tier_id !== 'free') {
        const memberTier = tiers?.find(t => t.id === member.tier_id);
        total += memberTier?.price || 0;
      }
    });
    return total;
  };

  // Helper to get full URL for images
  const getImageUrl = (url) => {
    if (!url) return null;
    // Skip invalid test URLs
    if (url.includes('test.com') || url.includes('example.com') || url.includes('placeholder')) return null;
    if (url.startsWith('http')) return url;
    // Check if URL already has /api prefix
    if (url.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    // Add /api prefix for proper routing through ingress
    return `${process.env.REACT_APP_BACKEND_URL}/api${url}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[color:var(--fwm-ground)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[color:var(--fwm-ink)]" />
      </div>
    );
  }

  if (!event) {
    return (
      <PublicLayout>
        <div className="flex flex-col items-center justify-center min-h-screen">
          <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold">Event not found</h2>
          <Button className="mt-4" onClick={() => navigate("/")}>
            Go Home
          </Button>
        </div>
      </PublicLayout>
    );
  }

  const primaryColor = event.branding?.primary_color || "#3B82F6";
  const secondaryColor = event.branding?.secondary_color || "#1E40AF";
  const selectedTier = tiers?.find((t) => t.id === formData.tier_id);
  const heroImage = getImageUrl(event.branding?.hero_image_url);
  const logoUrl = getImageUrl(event.branding?.logo_url);

  // Premium Landing Page (Step 0)
  if (step === 0) {
    return (
      <div className="fwm-page min-h-screen overflow-hidden">
        {/* Floating language switcher — picks up i18n language and persists to localStorage */}
        <div className="fixed top-4 right-4 z-[60]">
          <LanguageSwitcher variant="ghost" showLabel={false} />
        </div>
        {/* Global Urgency Bar - Sticky */}
        <div 
          className="fixed top-0 left-0 right-0 z-50 py-3 px-4 text-center text-sm font-medium
                     text-[#F7F1E3] bg-[color:var(--fwm-navy)]"
        >
          <div className="flex items-center justify-center gap-4">
            {countdown.expired ? (
              <>
                <span>Event has started</span>
                <span className="hidden sm:inline">•</span>
                <span className="hidden sm:inline">Late registration may still be available</span>
              </>
            ) : (
              <>
                <span className="animate-pulse">Registration closing soon</span>
                <span className="hidden sm:inline">•</span>
                <span className="hidden sm:flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {countdown.days}d {countdown.hours}h {countdown.minutes}m remaining
                </span>
              </>
            )}
            <button className="fwm-btn ml-4 py-1.5 px-4" data-testid="event-urgency-cta"
                    onClick={() => document.getElementById('tickets')?.scrollIntoView({ behavior: 'smooth' })}>
              {countdown.expired ? 'View options' : 'Register now'}
            </button>
          </div>
        </div>

        {/* Hero — the poster carries its own artwork and wording, so nothing
            is laid over it. Title, countdown and CTA sit in the panel below. */}
        {heroImage ? (
          <div className="pt-14">
            <img src={heroImage} alt={event.name} loading="eager"
                 data-testid="event-hero-image"
                 onLoad={(e) => setHeroUsable(e.currentTarget.naturalWidth > 4)}
                 onError={() => setHeroUsable(false)}
                 className={`w-full max-h-[78vh] object-contain bg-[color:var(--fwm-ground-2)] ${
                   heroUsable ? '' : 'hidden'}`} />
            <div className="border-t border-[color:var(--fwm-line)] bg-[color:var(--fwm-ground-2)]"
                 data-testid="event-hero-panel">
              <div className="max-w-5xl mx-auto px-4 py-10 sm:py-14">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[color:var(--fwm-muted)]">
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-4 w-4" /> {venueName(event.venue) || 'Virtual event'}
                  </span>
                  <span className="inline-flex items-center gap-1.5" data-testid="event-hero-dates">
                    <Clock className="h-4 w-4" />
                    {/* iter 635 — a four-day conference used to read as its
                        first day only, on the page people register from. */}
                    {publicRange(event.start_date, event.end_date)}
                  </span>
                  {(event.recurrence || 'none') !== 'none' && (
                    <span className="inline-flex items-center gap-1.5" data-testid="event-hero-recurrence">
                      <CalendarDays className="h-4 w-4" />
                      {{ weekly: 'Every week', monthly: 'Every month', yearly: 'Every year' }[event.recurrence]}
                    </span>
                  )}
                </div>
                <h1 className="mt-5 text-3xl sm:text-5xl font-bold tracking-tight leading-tight"
                    data-testid="event-hero-title">
                  {event.name}
                </h1>
                {/* iter 648d — an online or hybrid event is watched here. The
                    section removes itself when nothing is on air, so an
                    in-person event's hero is unchanged. */}
                <div className="mt-6 max-w-2xl">
                  <EventLiveSession eventId={eventId} />
                </div>
                {event.description && (
                  <p className="mt-4 max-w-2xl text-base sm:text-lg text-[color:var(--fwm-muted)]">
                    {event.description.slice(0, 180)}
                  </p>
                )}
                {/* iter 635 — a conference with a different timetable each day. */}
                {(event.programme || []).length > 0 && (
                  <div className="mt-6 max-w-2xl space-y-1" data-testid="event-programme">
                    <p className="text-xs uppercase tracking-wide text-[color:var(--fwm-muted)]">Programme</p>
                    {(event.programme || []).map((row, index) => (
                      <p key={index} className="text-sm text-[color:var(--fwm-ink)]"
                         data-testid={`event-programme-row-${index}`}>
                        <span className="font-semibold">{publicDay(row.date)}</span>
                        {row.start_time && ` · ${row.start_time}${row.end_time ? `–${row.end_time}` : ''}`}
                        {row.title && ` · ${row.title}`}
                      </p>
                    ))}
                  </div>
                )}

                <div className="mt-9 flex flex-wrap items-center gap-4 sm:gap-6">
                  {countdown.expired ? (
                    <div className="border border-[color:var(--fwm-line)] px-6 py-4" data-testid="event-countdown">
                      <p className="text-xl font-bold text-amber-400">Event has started</p>
                      <p className="text-sm text-[color:var(--fwm-muted)] mt-1">Late registration may still be available</p>
                    </div>
                  ) : (
                    <div className="flex gap-3 sm:gap-4" data-testid="event-countdown">
                      {[[countdown.days, 'Days'], [countdown.hours, 'Hours'],
                        [countdown.minutes, 'Minutes'], [countdown.seconds, 'Seconds']].map(([v, label]) => (
                          <div key={label} className="border border-[color:var(--fwm-line)] px-4 py-3 sm:px-5 sm:py-4 min-w-[72px] text-center">
                            <div className="text-2xl sm:text-3xl font-bold tabular-nums">
                              {String(v).padStart(2, '0')}
                            </div>
                            <div className="mt-1 text-[10px] sm:text-xs uppercase tracking-wider text-[color:var(--fwm-muted)]">
                              {label}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-3">
                    <button className="fwm-btn fwm-btn-lg h-14" data-testid="hero-cta"
                            onClick={() => document.getElementById('tickets')?.scrollIntoView({ behavior: 'smooth' })}>
                      Secure your spot <ArrowRight className="h-5 w-5" />
                    </button>
                    <AddToCalendar eventId={event.id || event._id} title="Save the date"
                                   className="h-14 rounded-none border-[color:var(--fwm-ink)]/25 bg-white/90 text-[color:var(--fwm-ink)] font-semibold hover:bg-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
        /* No poster uploaded — fall back to the typographic hero */
        <div className="relative min-h-screen flex flex-col items-center justify-center pt-16">
          {/* Glassmorphism Background */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Animated gradient blobs */}
            <div 
              className="absolute w-96 h-96 rounded-full blur-3xl opacity-30 animate-pulse"
              style={{ 
                background: `radial-gradient(circle, ${primaryColor}, transparent)`,
                top: '10%',
                left: '20%',
                animation: 'float 8s ease-in-out infinite'
              }}
            />
            <div 
              className="absolute w-80 h-80 rounded-full blur-3xl opacity-20 animate-pulse"
              style={{ 
                background: `radial-gradient(circle, ${secondaryColor}, transparent)`,
                bottom: '20%',
                right: '15%',
                animation: 'float 10s ease-in-out infinite reverse'
              }}
            />
            <div 
              className="absolute w-64 h-64 rounded-full blur-3xl opacity-25"
              style={{ 
                background: `radial-gradient(circle, #10B981, transparent)`,
                top: '50%',
                left: '60%',
                animation: 'float 12s ease-in-out infinite'
              }}
            />
          </div>

          {/* Content */}
          <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
            {/* Identity & Social Proof */}
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt={event.name} 
                className="h-20 mx-auto mb-8 animate-fade-in"
                style={{ animation: 'fadeSlideUp 0.6s ease-out' }}
              />
            ) : (
              <div 
                className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-[color:var(--fwm-card)] backdrop-blur-sm mb-8"
                style={{ animation: 'fadeSlideUp 0.6s ease-out' }}
              >
                <MapPin className="h-5 w-5" />
                <span>{event.venue?.name || 'Virtual Event'}</span>
                <span>•</span>
                <span>{new Date(event.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
            )}

            {/* Brutalist Typography - Event Title */}
            <h1 
              className="text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter leading-none mb-6"
              style={{ animation: 'fadeSlideUp 0.8s ease-out 0.2s both' }}
            >
              {event.name}
            </h1>

            {/* Subtitle/Description */}
            <p 
              className="text-xl sm:text-2xl text-[color:var(--fwm-body)] max-w-2xl mx-auto mb-12"
              style={{ animation: 'fadeSlideUp 0.8s ease-out 0.4s both' }}
            >
              {event.description?.slice(0, 150) || 'Join us for an unforgettable experience'}
            </p>

            {/* Matrix Countdown */}
            <div 
              className="flex justify-center gap-4 sm:gap-6 mb-12"
              style={{ animation: 'fadeSlideUp 0.8s ease-out 0.6s both' }}
            >
              {countdown.expired ? (
                <div className="bg-[color:var(--fwm-card)] backdrop-blur-sm rounded-2xl px-8 py-6">
                  <div className="text-2xl sm:text-3xl font-bold text-amber-400">
                    Event Has Started
                  </div>
                  <div className="text-sm text-[color:var(--fwm-muted)] mt-2">
                    Late registration may still be available
                  </div>
                </div>
              ) : (
                [
                  { value: countdown.days, label: 'Days' },
                  { value: countdown.hours, label: 'Hours' },
                  { value: countdown.minutes, label: 'Minutes' },
                  { value: countdown.seconds, label: 'Seconds' },
                ].map((item, i) => (
                  <div 
                    key={item.label}
                    className="bg-[color:var(--fwm-card)] backdrop-blur-sm rounded-2xl p-4 sm:p-6 min-w-[80px] sm:min-w-[100px]"
                  >
                    <div className="text-3xl sm:text-5xl font-bold tabular-nums">
                      {String(item.value).padStart(2, '0')}
                    </div>
                    <div className="text-xs sm:text-sm text-[color:var(--fwm-muted)] uppercase tracking-wider mt-1">
                      {item.label}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* CTA Button */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                size="lg"
                className="text-lg px-10 py-6 h-auto rounded-full font-semibold shadow-2xl transition-all hover:scale-105"
                style={{ 
                  backgroundColor: primaryColor,
                  animation: 'fadeSlideUp 0.8s ease-out 0.8s both'
                }}
                onClick={() => document.getElementById('tickets')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-cta"
              >
                Secure Your Spot <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <AddToCalendar eventId={event.id || event._id} title="Save the date"
                             className="rounded-full px-6 py-6 h-auto text-base border-[color:var(--fwm-ink)]/25 bg-white text-[color:var(--fwm-ink)] font-semibold hover:bg-[color:var(--fwm-ground-2)]" />
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronRight className="h-8 w-8 rotate-90 text-[color:var(--fwm-muted)]" />
          </div>
        </div>
        )}

        {/* Ticket Selection Section */}
        <div id="tickets" className="relative py-20 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Choose Your Experience</h2>
              <p className="text-[color:var(--fwm-muted)] text-lg">Click on a ticket to select it</p>
            </div>

            {/* Tier Cards - Premium Card Architecture */}
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {/* Free Registration / Register Interest Option - Always show */}
              <div
                className={cn(
                  "relative rounded-[32px] p-8 transition-all duration-300 cursor-pointer group",
                  "border-2 backdrop-blur-sm hover:scale-[1.01]",
                  formData.tier_id === 'free' 
                    ? "border-emerald-500 bg-emerald-500/10 scale-[1.02] ring-2 ring-emerald-500/50" 
                    : "border-[color:var(--fwm-line)] bg-[color:var(--fwm-card)] hover:bg-[color:var(--fwm-card)] hover:border-[color:var(--fwm-strong)]"
                )}
                style={{ animation: `fadeSlideUp 0.6s ease-out 0.1s both` }}
                onClick={() => setFormData({ ...formData, tier_id: 'free' })}
                data-testid="tier-free"
              >
                {/* Click to select hint */}
                {formData.tier_id !== 'free' && (
                  <div className="absolute top-4 right-4 text-xs text-[color:var(--fwm-muted)] group-hover:text-[color:var(--fwm-muted)] transition-colors">
                    Click to select
                  </div>
                )}
                
                {/* Selection indicator */}
                {formData.tier_id === 'free' && (
                  <div className="absolute top-6 right-6">
                    <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-2xl font-bold mb-2">Register Interest</h3>
                  <p className="text-[color:var(--fwm-muted)]">Sign up to receive updates about this event</p>
                </div>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold">Free</span>
                  </div>
                  <div className="text-sm text-[color:var(--fwm-muted)] mt-1">
                    No commitment required
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-[color:var(--fwm-body)]">Receive event updates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-[color:var(--fwm-body)]">Early access to announcements</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-[color:var(--fwm-body)]">No payment required</span>
                  </div>
                </div>
              </div>
              {tiers?.map((tier, index) => {
                const isVIP = tier.name.toLowerCase().includes('vip') || tier.price > 100;
                const isSelected = formData.tier_id === tier.id;
                const isSoldOut = tier.status === 'sold_out';
                
                return (
                  <div
                    key={tier.id}
                    className={cn(
                      "relative rounded-[32px] p-8 transition-all duration-300 cursor-pointer group",
                      "border-2 backdrop-blur-sm hover:scale-[1.01]",
                      isSelected 
                        ? "border-emerald-500 bg-emerald-500/10 scale-[1.02] ring-2 ring-emerald-500/50" 
                        : isVIP 
                          ? "border-amber-500/50 bg-[color:var(--fwm-card)] hover:bg-[color:var(--fwm-card)] hover:border-amber-500" 
                          : "border-[color:var(--fwm-line)] bg-[color:var(--fwm-card)] hover:bg-[color:var(--fwm-card)] hover:border-[color:var(--fwm-strong)]",
                      isSoldOut && "opacity-50 cursor-not-allowed"
                    )}
                    style={{ animation: `fadeSlideUp 0.6s ease-out ${0.2 + index * 0.1}s both` }}
                    onClick={() => !isSoldOut && setFormData({ ...formData, tier_id: tier.id })}
                    data-testid={`tier-${tier.id}`}
                  >
                    {/* Click to select hint */}
                    {!isSelected && !isSoldOut && (
                      <div className="absolute top-4 right-4 text-xs text-[color:var(--fwm-muted)] group-hover:text-[color:var(--fwm-muted)] transition-colors">
                        Click to select
                      </div>
                    )}
                    {/* VIP Badge */}
                    {isVIP && (
                      <div className="absolute -top-3 left-6">
                        <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-[color:var(--fwm-ink)] px-4 py-1 text-sm font-semibold">
                          <Star className="h-3 w-3 mr-1" /> Elite Access
                        </Badge>
                      </div>
                    )}

                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="absolute top-6 right-6">
                        <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                      </div>
                    )}

                    <div className="mb-6">
                      <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                      <p className="text-[color:var(--fwm-muted)]">{tier.description}</p>
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      <div className="flex items-baseline gap-2">
                        <span className="text-5xl font-bold">
                          {tier.price > 0 ? `$${tier.price}` : 'Free'}
                        </span>
                        {tier.price > 0 && <span className="text-[color:var(--fwm-muted)]">USD</span>}
                      </div>
                      <div className="text-sm text-[color:var(--fwm-muted)] mt-1">
                        {tier.available} of {tier.capacity} spots remaining
                      </div>
                    </div>

                    {/* Features - Check-Node List */}
                    <div className="space-y-3 mb-6">
                      {tier.features?.map((feature, fi) => (
                        <div key={fi} className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                          <span className="text-[color:var(--fwm-body)]">{feature}</span>
                        </div>
                      ))}
                      {tier.includes_meals && (
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                          <span className="text-[color:var(--fwm-body)]">All meals included</span>
                        </div>
                      )}
                      {tier.includes_housing && (
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                          <span className="text-[color:var(--fwm-body)]">Accommodation included</span>
                        </div>
                      )}
                      {tier.initial_coins > 0 && (
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                          <span className="text-[color:var(--fwm-body)]">{tier.initial_coins} EventCoins bonus</span>
                        </div>
                      )}
                    </div>

                    {/* Sold out badge */}
                    {isSoldOut && (
                      <Badge variant="destructive" className="absolute bottom-6 right-6">
                        Sold Out
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Registration Type Toggle */}
            <div className="mt-10 flex justify-center">
              <div className="inline-flex rounded-full p-1 bg-[color:var(--fwm-card)] backdrop-blur-sm">
                <button
                  className={cn(
                    "px-6 py-2 rounded-full text-sm font-medium transition-all",
                    registrationType === 'individual' 
                      ? "bg-white text-slate-900" 
                      : "text-[color:var(--fwm-body)] hover:text-[color:var(--fwm-ink)]"
                  )}
                  onClick={() => setRegistrationType('individual')}
                  data-testid="reg-type-individual"
                >
                  Individual
                </button>
                <button
                  className={cn(
                    "px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2",
                    registrationType === 'family' 
                      ? "bg-white text-slate-900" 
                      : "text-[color:var(--fwm-body)] hover:text-[color:var(--fwm-ink)]"
                  )}
                  onClick={() => setRegistrationType('family')}
                  data-testid="reg-type-family"
                >
                  <Users className="h-4 w-4" />
                  Family (up to 10)
                </button>
              </div>
            </div>

            {/* Continue Button */}
            <div className="mt-6 text-center">
              {!formData.tier_id && (
                <p className="mb-4 text-amber-400 text-sm animate-pulse">
                  👆 Please select an option above to continue
                </p>
              )}
              <Button
                size="lg"
                className={cn(
                  "text-lg px-12 py-6 h-auto rounded-full font-semibold transition-all",
                  !formData.tier_id && "opacity-50 cursor-not-allowed"
                )}
                style={{ backgroundColor: formData.tier_id ? primaryColor : '#64748b' }}
                disabled={!formData.tier_id}
                onClick={() => setStep(1)}
                data-testid="continue-to-details"
              >
                {formData.tier_id === 'free' ? 'Continue to Register Interest' : 'Continue to Registration'} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              
              <p className="mt-6 text-[color:var(--fwm-muted)] text-sm">
                Already registered?{' '}
                <Link to="/login" className="text-[color:var(--fwm-ink)] underline hover:no-underline">
                  Sign in here
                </Link>
                {' • '}
                <Link 
                  to={`/forgot-password?type=delegate&event=${event?.slug || eventId}`} 
                  className="text-[color:var(--fwm-body)] underline hover:no-underline hover:text-[color:var(--fwm-ink)]"
                >
                  Forgot password?
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Minimalist Footer */}
        <div className="py-8 border-t border-[color:var(--fwm-line)]">
          <div className="max-w-5xl mx-auto px-4 flex flex-wrap items-center justify-center gap-4 text-[color:var(--fwm-muted)] text-sm">
            <p>Powered by EventFlow • Secure checkout • Instant confirmation</p>
            <span className="hidden sm:inline">•</span>
            <AdminLoginButton context={{ eventId: event.id || event._id }} variant="inline"
                              label="Staff / Admin login"
                              className="text-[color:var(--fwm-muted)] hover:text-[color:var(--fwm-ink)]" />
          </div>
        </div>

        {/* Animation Keyframes */}
        <style>{`
          @keyframes fadeSlideUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0) scale(1); }
            50% { transform: translateY(-20px) scale(1.05); }
          }
        `}</style>
      </div>
    );
  }

  // Registration Steps (Steps 1-4) - Keep existing but with dark theme wrapper
  return (
    <div className="fwm-page min-h-screen">
      {/* Sticky header for registration steps */}
      <div className="sticky top-0 z-50 py-4 px-4 bg-[color:var(--fwm-navy)] text-[#F7F1E3]">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="h-8" />
            ) : (
              <span className="font-bold">{event.name}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="ghost" showLabel={false} />
            <Button variant="ghost" className="text-[#F7F1E3] hover:bg-white/10" onClick={handleCancelRegistration}>
              <X className="h-4 w-4 mr-2" /> Cancel
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <EventLiveSession eventId={eventId} />
        {/* External Google Form (when admin has configured one) */}
        {externalForm?.enabled && externalForm.form_url && (
          <div className="mb-6" data-testid="external-form-section">
            {externalForm.display_mode === 'redirect' ? (
              <Card className="bg-emerald-950/40 border-emerald-700">
                <CardContent className="p-6 text-center text-[color:var(--fwm-ink)]">
                  <h3 className="text-lg font-semibold mb-2">Register on Google Forms</h3>
                  <p className="text-sm text-[color:var(--fwm-body)] mb-4">This event uses Google Forms for registration. Click below to complete it.</p>
                  <a
                    href={externalForm.form_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
                    data-testid="external-form-redirect-link"
                  >
                    Open Registration Form <ArrowRight className="h-4 w-4" />
                  </a>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[color:var(--fwm-ground-2)] border-[color:var(--fwm-line)] overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-[color:var(--fwm-ink)] text-base">Registration Form</CardTitle>
                  <CardDescription className="text-[color:var(--fwm-muted)]">Please complete the form below.</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <iframe
                    src={externalForm.embed_url}
                    title="Registration form"
                    width="100%"
                    height="700"
                    frameBorder="0"
                    marginHeight="0"
                    marginWidth="0"
                    data-testid="external-form-embed-iframe"
                  >
                    Loading…
                  </iframe>
                </CardContent>
              </Card>
            )}
            {!externalForm.show_native_form && (
              <div className="text-center mt-4">
                <a href={externalForm.form_url} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-400 hover:underline">
                  Trouble loading? Open the form directly →
                </a>
              </div>
            )}
          </div>
        )}

        {/* Native EventFlow form — hidden when external is enabled in embed/redirect mode unless admin opted to show both */}
        {(!externalForm?.enabled || externalForm.show_native_form) && (
        <>
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {["Ticket", "Details", "Meals", "Review", "Payment"].map((label, i) => (
              <div key={i} className="flex items-center">
                <div
                  className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                    step > i ? "bg-[color:var(--fwm-navy)] text-[#F7F1E3]" : step === i ? "bg-[color:var(--fwm-accent)] text-[color:var(--fwm-ink)]" : "bg-[color:var(--fwm-ground-2)] text-[color:var(--fwm-muted)]"
                  )}
                >
                  {step > i ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                <span className="ml-2 text-sm hidden sm:inline text-[color:var(--fwm-body)]">{label}</span>
                {i < 4 && <ChevronRight className="h-4 w-4 text-slate-600 mx-2" />}
              </div>
            ))}
          </div>
          <Progress value={(step / 5) * 100} className="h-2 bg-[color:var(--fwm-ground-2)]" />
        </div>

        {/* Step 1: Identity */}
        {step === 1 && (
          <Card className="bg-[color:var(--fwm-ground-2)] border-[color:var(--fwm-line)]">
            <CardHeader>
              <CardTitle className="text-[color:var(--fwm-ink)]">Your Information</CardTitle>
              <CardDescription className="text-[color:var(--fwm-muted)]">Tell us about yourself</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name" className="text-[color:var(--fwm-ink)]">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    data-testid="input-first-name"
                    className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name" className="text-[color:var(--fwm-ink)]">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    data-testid="input-last-name"
                    className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="gender" className="text-[color:var(--fwm-ink)]">Gender *</Label>
                <Select 
                  value={formData.gender} 
                  onValueChange={(v) => setFormData({ ...formData, gender: v })}
                >
                  <SelectTrigger 
                    id="gender"
                    data-testid="select-gender"
                    className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)]"
                  >
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="email" className="text-[color:var(--fwm-ink)]">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="input-email"
                  className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-[color:var(--fwm-ink)]">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="input-password"
                  className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                />
                <p className="text-xs text-[color:var(--fwm-muted)] mt-1">You will use this to access your delegate portal</p>
              </div>
              {fieldCfg('phone').enabled && (
                <div>
                  <Label htmlFor="phone" className="text-[color:var(--fwm-ink)]">{fieldCfg('phone').label}{star('phone')}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required={fieldCfg('phone').required}
                    data-testid="input-phone"
                    className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="province" className="text-[color:var(--fwm-ink)]">Province / State</Label>
                  <Input
                    id="province"
                    value={formData.province || ''}
                    onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                    data-testid="input-province"
                    className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                    placeholder="Your province"
                  />
                </div>
                <div>
                  <Label htmlFor="district" className="text-[color:var(--fwm-ink)]">District</Label>
                  <Input
                    id="district"
                    value={formData.district || ''}
                    onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    data-testid="input-district"
                    className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                    placeholder="Your district"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date_of_birth" className="text-[color:var(--fwm-ink)]">Date of Birth</Label>
                  <Input
                    id="date_of_birth"
                    type="date"
                    value={formData.date_of_birth || ''}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    data-testid="input-dob"
                    className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                  />
                </div>
                <div>
                  <Label htmlFor="age_group" className="text-[color:var(--fwm-ink)]">Age Group</Label>
                  <Select 
                    value={formData.age_group || 'auto'} 
                    onValueChange={(v) => setFormData({ ...formData, age_group: v === 'auto' ? '' : v })}
                  >
                    <SelectTrigger 
                      id="age_group"
                      data-testid="select-age-group"
                      className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)]"
                    >
                      <SelectValue placeholder="Auto from DOB" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto-detect from DOB</SelectItem>
                      <SelectItem value="under-10">Under 10</SelectItem>
                      <SelectItem value="10-12">10-12</SelectItem>
                      <SelectItem value="13-15">13-15</SelectItem>
                      <SelectItem value="16-18">16-18</SelectItem>
                      <SelectItem value="19-25">19-25</SelectItem>
                      <SelectItem value="26-35">26-35</SelectItem>
                      <SelectItem value="36+">36+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Separator className="bg-[color:var(--fwm-line)]" />
              {fieldCfg('company').enabled && (
                <div>
                  <Label htmlFor="company" className="text-[color:var(--fwm-ink)]">{fieldCfg('company').label}{star('company')}</Label>
                  <Input
                    id="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    required={fieldCfg('company').required}
                    data-testid="input-company"
                    className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                  />
                </div>
              )}
              {fieldCfg('job_title').enabled && (
                <div>
                  <Label htmlFor="title" className="text-[color:var(--fwm-ink)]">{fieldCfg('job_title').label}{star('job_title')}</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required={fieldCfg('job_title').required}
                    data-testid="input-title"
                    className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                  />
                </div>
              )}
              {/* iter 634 — these four were configurable in the builder and had
                  no input here at all, so switching them on changed nothing. */}
              {(fieldCfg('country').enabled || fieldCfg('city').enabled) && (
                <div className="grid grid-cols-2 gap-4">
                  {fieldCfg('country').enabled && (
                    <div>
                      <Label htmlFor="country" className="text-[color:var(--fwm-ink)]">{fieldCfg('country').label}{star('country')}</Label>
                      <Input
                        id="country"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        required={fieldCfg('country').required}
                        data-testid="input-country"
                        className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                      />
                    </div>
                  )}
                  {fieldCfg('city').enabled && (
                    <div>
                      <Label htmlFor="city" className="text-[color:var(--fwm-ink)]">{fieldCfg('city').label}{star('city')}</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        required={fieldCfg('city').required}
                        data-testid="input-city"
                        className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                      />
                    </div>
                  )}
                </div>
              )}
              {fieldCfg('dietary_requirements').enabled && (
                <div>
                  <Label htmlFor="dietary_requirements" className="text-[color:var(--fwm-ink)]">{fieldCfg('dietary_requirements').label}{star('dietary_requirements')}</Label>
                  <Input
                    id="dietary_requirements"
                    value={formData.dietary_requirements}
                    onChange={(e) => setFormData({ ...formData, dietary_requirements: e.target.value })}
                    required={fieldCfg('dietary_requirements').required}
                    placeholder="Allergies or dietary needs"
                    data-testid="input-dietary-requirements"
                    className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                  />
                </div>
              )}
              {fieldCfg('special_needs').enabled && (
                <div>
                  <Label htmlFor="special_needs" className="text-[color:var(--fwm-ink)]">{fieldCfg('special_needs').label}{star('special_needs')}</Label>
                  <Textarea
                    id="special_needs"
                    value={formData.special_needs}
                    onChange={(e) => setFormData({ ...formData, special_needs: e.target.value })}
                    required={fieldCfg('special_needs').required}
                    placeholder="Anything we should arrange for you"
                    data-testid="input-special-needs"
                    className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="bio" className="text-[color:var(--fwm-ink)]">Short Bio</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Tell others a bit about yourself..."
                  data-testid="input-bio"
                  className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                />
              </div>

              {/* Custom Registration Fields */}
              {customFields.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-700">
                  <Label className="text-[color:var(--fwm-ink)] text-lg mb-4 block">Additional Information</Label>
                  <div className="space-y-4">
                    {customFields.map((field) => (
                      <div key={field.id}>
                        <Label htmlFor={`custom-${field.id}`} className="text-[color:var(--fwm-ink)]">
                          {field.label} {field.required && '*'}
                        </Label>
                        
                        {/* Text Input */}
                        {field.field_type === 'text' && (
                          <Input
                            id={`custom-${field.id}`}
                            value={formData.custom_responses[field.id] || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              custom_responses: { ...formData.custom_responses, [field.id]: e.target.value }
                            })}
                            placeholder={field.placeholder}
                            required={field.required}
                            data-testid={`custom-field-${field.id}`}
                            className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                          />
                        )}

                        {/* Textarea */}
                        {field.field_type === 'textarea' && (
                          <Textarea
                            id={`custom-${field.id}`}
                            value={formData.custom_responses[field.id] || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              custom_responses: { ...formData.custom_responses, [field.id]: e.target.value }
                            })}
                            placeholder={field.placeholder}
                            required={field.required}
                            data-testid={`custom-field-${field.id}`}
                            className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                          />
                        )}

                        {/* Dropdown */}
                        {field.field_type === 'dropdown' && (
                          <Select
                            value={formData.custom_responses[field.id] || ''}
                            onValueChange={(val) => setFormData({
                              ...formData,
                              custom_responses: { ...formData.custom_responses, [field.id]: val }
                            })}
                          >
                            <SelectTrigger 
                              className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)]"
                              data-testid={`custom-field-${field.id}`}
                            >
                              <SelectValue placeholder={field.placeholder || 'Select an option'} />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options?.map((option, idx) => (
                                <SelectItem key={idx} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}

                        {/* Single Choice (Radio-style using buttons) */}
                        {field.field_type === 'single_choice' && (
                          <div className="flex flex-wrap gap-2 mt-2" data-testid={`custom-field-${field.id}`}>
                            {field.options?.map((option, idx) => (
                              <Button
                                key={idx}
                                type="button"
                                variant={formData.custom_responses[field.id] === option.value ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setFormData({
                                  ...formData,
                                  custom_responses: { ...formData.custom_responses, [field.id]: option.value }
                                })}
                                className={formData.custom_responses[field.id] === option.value 
                                  ? 'bg-emerald-600 hover:bg-emerald-700' 
                                  : 'border-[color:var(--fwm-line)] text-[color:var(--fwm-body)] hover:bg-[color:var(--fwm-hover)]'}
                              >
                                {option.label}
                              </Button>
                            ))}
                          </div>
                        )}

                        {/* Checkbox */}
                        {field.field_type === 'checkbox' && (
                          <div className="flex items-center gap-2 mt-2">
                            <Checkbox
                              id={`custom-${field.id}`}
                              checked={formData.custom_responses[field.id] || false}
                              onCheckedChange={(checked) => setFormData({
                                ...formData,
                                custom_responses: { ...formData.custom_responses, [field.id]: checked }
                              })}
                              data-testid={`custom-field-${field.id}`}
                            />
                            <Label htmlFor={`custom-${field.id}`} className="text-[color:var(--fwm-body)] text-sm">
                              {field.placeholder || 'Yes'}
                            </Label>
                          </div>
                        )}

                        {/* Number */}
                        {field.field_type === 'number' && (
                          <Input
                            id={`custom-${field.id}`}
                            type="number"
                            value={formData.custom_responses[field.id] || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              custom_responses: { ...formData.custom_responses, [field.id]: e.target.value }
                            })}
                            placeholder={field.placeholder}
                            required={field.required}
                            data-testid={`custom-field-${field.id}`}
                            className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                          />
                        )}

                        {/* Email */}
                        {field.field_type === 'email' && (
                          <Input
                            id={`custom-${field.id}`}
                            type="email"
                            value={formData.custom_responses[field.id] || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              custom_responses: { ...formData.custom_responses, [field.id]: e.target.value }
                            })}
                            placeholder={field.placeholder}
                            required={field.required}
                            data-testid={`custom-field-${field.id}`}
                            className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                          />
                        )}

                        {/* Phone */}
                        {field.field_type === 'phone' && (
                          <Input
                            id={`custom-${field.id}`}
                            type="tel"
                            value={formData.custom_responses[field.id] || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              custom_responses: { ...formData.custom_responses, [field.id]: e.target.value }
                            })}
                            placeholder={field.placeholder}
                            required={field.required}
                            data-testid={`custom-field-${field.id}`}
                            className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                          />
                        )}

                        {/* Date */}
                        {field.field_type === 'date' && (
                          <Input
                            id={`custom-${field.id}`}
                            type="date"
                            value={formData.custom_responses[field.id] || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              custom_responses: { ...formData.custom_responses, [field.id]: e.target.value }
                            })}
                            required={field.required}
                            data-testid={`custom-field-${field.id}`}
                            className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)]"
                          />
                        )}

                        {/* Help Text */}
                        {field.help_text && (
                          <p className="text-xs text-[color:var(--fwm-muted)] mt-1">{field.help_text}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Family Members Section - Only show if family registration selected */}
              {registrationType === 'family' && (
                <div className="mt-6 pt-6 border-t border-slate-700">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Label className="text-[color:var(--fwm-ink)] text-lg">Family Members</Label>
                      <p className="text-sm text-[color:var(--fwm-muted)]">Add up to 9 additional family members</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addFamilyMember}
                      disabled={familyMembers.length >= 9}
                      className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/20"
                      data-testid="add-family-member-btn"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Member
                    </Button>
                  </div>

                  {familyMembers.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed border-slate-700 rounded-lg">
                      <Users className="h-12 w-12 mx-auto text-slate-500 mb-3" />
                      <p className="text-[color:var(--fwm-muted)]">No family members added yet</p>
                      <p className="text-sm text-slate-500 mt-1">Click "Add Member" to register family</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {familyMembers.map((member, index) => (
                        <div key={index} className="p-4 bg-[color:var(--fwm-card)] rounded-lg border border-slate-700">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-[color:var(--fwm-ink)] font-medium">Family Member {index + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFamilyMember(index)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                              data-testid={`remove-member-${index}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-[color:var(--fwm-body)] text-sm">Name *</Label>
                              <Input
                                value={member.name}
                                onChange={(e) => updateFamilyMember(index, 'name', e.target.value)}
                                placeholder="Full name"
                                className="bg-[color:var(--fwm-card)] border-slate-600 text-[color:var(--fwm-ink)] text-sm"
                                data-testid={`member-name-${index}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[color:var(--fwm-body)] text-sm">Relationship</Label>
                              <Select
                                value={member.relationship}
                                onValueChange={(val) => updateFamilyMember(index, 'relationship', val)}
                              >
                                <SelectTrigger className="bg-[color:var(--fwm-card)] border-slate-600 text-[color:var(--fwm-ink)] text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="spouse">Spouse</SelectItem>
                                  <SelectItem value="child">Child</SelectItem>
                                  <SelectItem value="parent">Parent</SelectItem>
                                  <SelectItem value="sibling">Sibling</SelectItem>
                                  <SelectItem value="family">Other Family</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[color:var(--fwm-body)] text-sm">Email (optional)</Label>
                              <Input
                                value={member.email}
                                onChange={(e) => updateFamilyMember(index, 'email', e.target.value)}
                                placeholder="email@example.com"
                                className="bg-[color:var(--fwm-card)] border-slate-600 text-[color:var(--fwm-ink)] text-sm"
                                data-testid={`member-email-${index}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[color:var(--fwm-body)] text-sm">Ticket Tier</Label>
                              <Select
                                value={member.tier_id}
                                onValueChange={(val) => updateFamilyMember(index, 'tier_id', val)}
                              >
                                <SelectTrigger className="bg-[color:var(--fwm-card)] border-slate-600 text-[color:var(--fwm-ink)] text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="free">Free Registration</SelectItem>
                                  {tiers?.map((tier) => (
                                    <SelectItem key={tier.id} value={tier.id}>
                                      {tier.name} - {event.currency || '$'}{tier.price}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      ))}
                      {/* Family Total */}
                      <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                        <div className="flex justify-between items-center text-emerald-400">
                          <span className="font-medium">Family Total ({familyMembers.length + 1} members)</span>
                          <span className="text-xl font-bold">{event.currency || '$'}{calculateFamilyTotal().toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Vehicle Registration Section - Optional */}
              <div className="mt-6 pt-6 border-t border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="text-[color:var(--fwm-ink)] text-lg flex items-center gap-2">
                      🚗 Vehicle Registration
                      <Badge variant="outline" className="text-[color:var(--fwm-muted)] border-slate-600 text-xs">Optional</Badge>
                    </Label>
                    <p className="text-sm text-[color:var(--fwm-muted)]">Pre-register your vehicle for event grounds access</p>
                  </div>
                  <Checkbox
                    id="add-vehicle"
                    checked={formData.addVehicle || false}
                    onCheckedChange={(checked) => setFormData({ 
                      ...formData, 
                      addVehicle: checked,
                      vehicle: checked ? (formData.vehicle || { license_plate: '', make: '', model: '', color: '' }) : null
                    })}
                    className="border-slate-500 data-[state=checked]:bg-emerald-600"
                  />
                </div>
                
                {formData.addVehicle && (
                  <div className="space-y-4 p-4 bg-[color:var(--fwm-card)] rounded-lg border border-slate-700">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="license_plate" className="text-[color:var(--fwm-ink)]">License Plate *</Label>
                        <Input
                          id="license_plate"
                          value={formData.vehicle?.license_plate || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            vehicle: { ...formData.vehicle, license_plate: e.target.value.toUpperCase() }
                          })}
                          placeholder="e.g., ABC 1234"
                          data-testid="vehicle-license-plate"
                          className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="vehicle_color" className="text-[color:var(--fwm-ink)]">Color</Label>
                        <Input
                          id="vehicle_color"
                          value={formData.vehicle?.color || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            vehicle: { ...formData.vehicle, color: e.target.value }
                          })}
                          placeholder="e.g., Black, White"
                          data-testid="vehicle-color"
                          className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="vehicle_make" className="text-[color:var(--fwm-ink)]">Make</Label>
                        <Input
                          id="vehicle_make"
                          value={formData.vehicle?.make || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            vehicle: { ...formData.vehicle, make: e.target.value }
                          })}
                          placeholder="e.g., Toyota, Honda"
                          data-testid="vehicle-make"
                          className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                        />
                      </div>
                      <div>
                        <Label htmlFor="vehicle_model" className="text-[color:var(--fwm-ink)]">Model</Label>
                        <Input
                          id="vehicle_model"
                          value={formData.vehicle?.model || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            vehicle: { ...formData.vehicle, model: e.target.value }
                          })}
                          placeholder="e.g., Camry, Civic"
                          data-testid="vehicle-model"
                          className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="parking_notes" className="text-[color:var(--fwm-ink)]">Parking / Access Notes</Label>
                      <Textarea
                        id="parking_notes"
                        value={formData.vehicle?.parking_notes || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          vehicle: { ...formData.vehicle, parking_notes: e.target.value }
                        })}
                        placeholder="Any special parking requirements or access notes..."
                        data-testid="vehicle-notes"
                        className="bg-[color:var(--fwm-card)] border-slate-700 text-[color:var(--fwm-ink)] placeholder:text-slate-500"
                        rows={2}
                      />
                    </div>
                    <p className="text-xs text-[color:var(--fwm-muted)]">
                      💡 Your vehicle will be pre-registered for event grounds access. Security staff will be able to verify your vehicle at entry points.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex justify-between border-t border-[color:var(--fwm-line)] pt-4">
              <Button variant="outline" onClick={handleCancelRegistration} className="border-slate-700 text-[color:var(--fwm-ink)] hover:bg-[color:var(--fwm-card)]">
                <ChevronLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              {requiredGaps.length > 0 && (
                <p className="text-xs text-amber-400 self-center" data-testid="required-fields-hint">
                  Still needed: {requiredGaps.map((id) => fieldCfg(id).label).join(', ')}
                </p>
              )}
              <Button
                style={{ backgroundColor: primaryColor }}
                disabled={!formData.email || !formData.password || !formData.first_name || !formData.last_name || !formData.gender || requiredGaps.length > 0}
                onClick={() => setStep(2)}
                data-testid="continue-to-meals"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Step 2: Provisioning (Meals) */}
        {step === 2 && (
          <Card className="bg-[color:var(--fwm-ground-2)] border-[color:var(--fwm-line)]">
            <CardHeader>
              <CardTitle className="text-[color:var(--fwm-ink)]">Meal Selections</CardTitle>
              <CardDescription className="text-[color:var(--fwm-muted)]">
                {selectedTier?.includes_meals
                  ? "Select your meal preferences for each day"
                  : "Your ticket does not include meals"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedTier?.includes_meals ? (
                <MealSelectionForm
                  event={event}
                  selections={formData.meal_selections}
                  onChange={(selections) => setFormData({ ...formData, meal_selections: selections })}
                />
              ) : (
                <div className="text-center py-8 text-[color:var(--fwm-muted)]">
                  <Utensils className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Meals are not included in your ticket tier.</p>
                  <p className="text-sm mt-2 text-slate-500">You can purchase meals on-site.</p>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-between border-t border-[color:var(--fwm-line)] pt-4">
              <Button variant="outline" onClick={() => setStep(1)} className="border-slate-700 text-[color:var(--fwm-ink)] hover:bg-[color:var(--fwm-card)]">
                <ChevronLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                style={{ backgroundColor: primaryColor }}
                onClick={() => setStep(3)}
                data-testid="continue-to-review"
              >
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <Card className="bg-[color:var(--fwm-ground-2)] border-[color:var(--fwm-line)]">
            <CardHeader>
              <CardTitle className="text-[color:var(--fwm-ink)]">Review & Confirm</CardTitle>
              <CardDescription className="text-[color:var(--fwm-muted)]">Please review your registration details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Ticket Summary */}
              <div className="p-4 bg-[color:var(--fwm-card)] rounded-lg">
                <h4 className="font-medium mb-2 text-[color:var(--fwm-ink)]">Ticket</h4>
                <div className="flex justify-between text-[color:var(--fwm-body)]">
                  <span>{formData.tier_id === 'free' ? 'Register Interest' : selectedTier?.name}</span>
                  <span className="font-semibold text-[color:var(--fwm-ink)]">
                    {formData.tier_id === 'free' || !selectedTier?.price ? "Free" : `$${selectedTier.price}`}
                  </span>
                </div>
              </div>

              {/* Personal Info */}
              <div>
                <h4 className="font-medium mb-2 text-[color:var(--fwm-ink)]">Personal Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-[color:var(--fwm-muted)]">Name</div>
                  <div className="text-[color:var(--fwm-ink)]">{formData.first_name} {formData.last_name}</div>
                  <div className="text-[color:var(--fwm-muted)]">Email</div>
                  <div className="text-[color:var(--fwm-ink)]">{formData.email}</div>
                  {formData.company && (
                    <>
                      <div className="text-[color:var(--fwm-muted)]">Company</div>
                      <div className="text-[color:var(--fwm-ink)]">{formData.company}</div>
                    </>
                  )}
                </div>
              </div>

              {/* Meals */}
              {formData.meal_selections.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 text-[color:var(--fwm-ink)]">Meal Selections</h4>
                  <div className="text-sm text-[color:var(--fwm-muted)]">
                    {formData.meal_selections.length} meals selected
                  </div>
                </div>
              )}

              {/* Terms */}
              <div className="flex items-start gap-2">
                <Checkbox id="terms" required className="border-slate-600" />
                <Label htmlFor="terms" className="text-sm text-[color:var(--fwm-muted)]">
                  I agree to the event terms and conditions and privacy policy.
                </Label>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between border-t border-[color:var(--fwm-line)] pt-4">
              <Button variant="outline" onClick={() => setStep(2)} className="border-slate-700 text-[color:var(--fwm-ink)] hover:bg-[color:var(--fwm-card)]">
                <ChevronLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                style={{ backgroundColor: primaryColor }}
                onClick={handleSubmit}
                disabled={isSubmitting}
                data-testid="complete-registration"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {selectedTier?.price > 0 ? `Continue to Payment` : "Complete Registration"}
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Step 4: Payment */}
        {step === 4 && pendingPayment && (
          <PaymentGatewaySelector
            eventId={event.id}
            delegateId={pendingPayment.delegate_id}
            tierId={pendingPayment.tier_id}
            delegateEmail={formData.email}
            delegateName={formData.name}
            amount={pendingPayment.amount}
            currency={pendingPayment.currency}
            onPaymentInitialized={handlePaymentInitialized}
            onPaymentComplete={handlePaymentComplete}
            onCancel={handlePaymentCancel}
          />
        )}
        </>
        )}
      </div>
      
      {/* Mobile App Install Prompt */}
      <InstallAppPrompt />
    </div>
  );
};

export default EventLanding;
